import { useReceiptStore } from "@elixir/stores";
import { PaymodeSelector, PopupWrapper, WarningStrip } from "ui";
import { getSettleDueReceiptPayload } from "@elixir/utils";
import { useMemo } from "react";
import { CircleExclamationRegularIcon } from "@elixir/icons";
import { RECEIPT_FLOW_STATES } from "@elixir/types";
import usePostCreateReceipt from "../../apis/tryst/create-receipt";
import { closeReceiptPopupEvent, viewReceipt } from "../../utils/receipt";
import ReceiptInputParams from "./input-fields/receipt-input-params";
import RemarksInput from "./input-fields/remarks";
import DueAmountInput from "./input-fields/due-amount-input";
import PaymentIdInputField from "./input-fields/payment-id";
import { useMyAccessProfileV2 } from "../../apis/tryst/get-my-access-profile";
import { PROGRESS_STEPS } from "@elixir/stores/src/receipt-store";
import { docListMassaged } from "../receipt-customisation/customisation-utls";

export default function SettleDueForm() {
  const onCloseReceiptPopup = useReceiptStore(
    (state) => state.setCloseReceiptPopup
  );
  const receiptState = useReceiptStore((state) => state.receiptState);
  const paymodes = useReceiptStore((state) => state.paymodes);
  const setPaymodes = useReceiptStore((state) => state.setPaymodes);
  const amountSettled = useReceiptStore((state) => state.amountSettled);

  const { mutateAsync: createNewReceipt, isLoading: isReceiptCreating } =
    usePostCreateReceipt();

  const { valid, error } = useMemo(() => {
    let paymodeSum = 0;
    for (let i = 0; i < paymodes.length; i++) {
      const paymodeAmount = paymodes[i].amount;
      if (paymodeAmount < 0) {
        return { valid: false, error: "Paymode Amount cannot be less than 0" };
      }
      paymodeSum += paymodeAmount;
    }

    if (paymodeSum !== amountSettled) {
      return {
        valid: false,
        error: "Paymode Amount's sum should be equal to due being settled",
      };
    }

    return { valid: true };
  }, [paymodes, amountSettled]);

  const { data: myAccessProfile } = useMyAccessProfileV2();
  const allProfiles = docListMassaged();
  const selectedAppointment = useReceiptStore(
    (state) => state.selectedAppointment
  );
  const activeDoctor = allProfiles?.doctors.find(
    (doc) => selectedAppointment?.doctor_oid == doc.oid
  );
  return (
    <PopupWrapper
      title="Settle Dues"
      onClose={() => {
        onCloseReceiptPopup();
        closeReceiptPopupEvent();
      }}
      open={receiptState === RECEIPT_FLOW_STATES.SETTLE_DUES}
      cancelText="Cancel"
      onOk={async () => {
        const receiptStore = useReceiptStore.getState();
        const { setCurrentStep, setIsProcessing } = useReceiptStore.getState();
        const receiptBody = getSettleDueReceiptPayload(receiptStore);
        const { success, data } = await createNewReceipt({
          ...receiptBody,
          businessId: myAccessProfile?.business?.id!,
          doctor_oid: receiptStore.doctor_oid || activeDoctor?.oid,
        });
        if (success && data) {
          setIsProcessing(true);
          setCurrentStep(PROGRESS_STEPS.CREATING_RECEIPT);
          viewReceipt({ receipt: data });
        }
      }}
      style={{ height: "40vh" }}
      okText="Settle Dues"
      isOkButtonDisabled={!valid}
      isSubmitting={isReceiptCreating}
    >
      <div className="dr-flex dr-flex-col dr-h-full dr-px-24 dr-overflow-scroll">
        {error ? (
          <div className="dr-sticky dr-top-0 dr-z-50 dr-bg-darwin-neutral-0">
            <WarningStrip>
              <div className="dr-flex dr-items-center dr-space-x-2">
                <CircleExclamationRegularIcon className="dr-w-12 dr-h-12" />
                <p className="BodyRegular">{error}</p>
              </div>
            </WarningStrip>
          </div>
        ) : null}
        <ReceiptInputParams />
        <div className="dr-flex dr-flex-col dr-justify-between dr-h-full dr-px-16">
          <div className="dr-flex dr-flex-col dr-space-y-24">
            <DueAmountInput />
            <PaymodeSelector
              paymodes={paymodes.map((p) => ({
                ...p,
                amountToShowWithType: String(p.amount),
              }))}
              amountDisabled={paymodes.length !== 2}
              setPaymodes={(updatedPaymodes, updatedIndex) => {
                if (typeof updatedIndex === "number") {
                  const updatedPaymode = updatedPaymodes[updatedIndex];
                  const indexToChange = updatedIndex === 0 ? 1 : 0;

                  if (updatedPaymodes[indexToChange]) {
                    updatedPaymodes[indexToChange] = {
                      ...updatedPaymodes[indexToChange],
                      amount: (amountSettled || 0) - updatedPaymode.amount,
                      amountToShowWithType: String(
                        (amountSettled || 0) - updatedPaymode.amount
                      ),
                    };
                  }
                }

                if (updatedPaymodes.length === 1) {
                  setPaymodes([
                    { ...updatedPaymodes[0], amount: amountSettled || 0 },
                  ]);
                }

                setPaymodes(updatedPaymodes);
              }}
            />
            <PaymentIdInputField />
          </div>
          <RemarksInput />
        </div>
      </div>
    </PopupWrapper>
  );
}
